import { useState } from 'react'
import { setInitialEstablishment } from 'reduxStore/slices'
import { useAppDispatch } from 'reduxStore/store'
import { authenticate, firebaseSignInWithCustomToken } from 'services/auth'
import { session } from 'services/user'
import Storage from 'storage/auth'

const ERROR_MESSAGES = {
  INVALID_CREDENTIALS: 'Erro de usuário e/ou senha',
  NO_CONNECTION: 'Erro de falta de conexão',
  SERVICE_UNAVAILABLE: 'Serviço fora do ar',
  UNEXPECTED: 'Erro inesperado'
} as const

const useLogin = () => {
  const [state, setState] = useState<IStateStdTypes>('idle')
  const [message, setMessage] = useState<string>('')
  const dispatch = useAppDispatch()

  const _saveToken = (
    token: string,
    refreshToken: string,
    customToken: string
  ) => {
    Storage.token.set(token)
    Storage.refreshToken.set(refreshToken)
    Storage.customToken.set(customToken)
  }

  const _getSession = async () => {
    const response = await session()

    if (!response.success || !response.data) {
      throw new Error('SESSION_FAILED')
    }

    if (response.data.user_type === 'patient') {
      throw new Error('INVALID_CREDENTIALS')
    }

    Storage.user.set(response.data)

    const sessionInfo = response.data.session_info
    if (sessionInfo?.current_establishment_id) {
      dispatch(setInitialEstablishment(sessionInfo.current_establishment_id))
    }
  }

  const isNetworkError = (error: any): boolean => {
    return (
      error?.message === 'Network Error' ||
      error?.code === 'ERR_NETWORK' ||
      error?.code === 'ECONNABORTED' ||
      error?.message?.toLowerCase().includes('timeout') ||
      error?.message?.toLowerCase().includes('network') ||
      !navigator.onLine
    )
  }

  const isServerUnavailable = (error: any, response?: any): boolean => {
    const status = error?.response?.status || response?.status
    if (status >= 500 && status < 600) {
      return true
    }

    return (
      error?.code === 'ECONNREFUSED' ||
      error?.code === 'ENOTFOUND' ||
      error?.code === 'ERR_BAD_RESPONSE'
    )
  }

  const getErrorMessage = (error: any, response?: any): string => {
    if (isNetworkError(error)) {
      return ERROR_MESSAGES.NO_CONNECTION
    }

    if (isServerUnavailable(error, response)) {
      return ERROR_MESSAGES.SERVICE_UNAVAILABLE
    }

    if (error instanceof Error) {
      switch (error.message) {
        case 'INVALID_CREDENTIALS':
        case 'UNAUTHORIZED':
        case 'AUTH_FAILED':
        case 'FIREBASE_AUTH_FAILED':
          return ERROR_MESSAGES.INVALID_CREDENTIALS
        default:
          break
      }
    }

    return ERROR_MESSAGES.UNEXPECTED
  }

  const login = async (email: string, password: string) => {
    setState('loading')
    setMessage('')

    try {
      const response = await authenticate({ email, password })

      if (isServerUnavailable(null, response)) {
        throw new Error('SERVICE_UNAVAILABLE')
      }

      if (!response.success || !response.data?.custom_token) {
        if (
          response.errors?.some(
            err => err.detail === 'Invalid user or password'
          ) ||
          response.status === 401 ||
          response.status === 403
        ) {
          throw new Error('INVALID_CREDENTIALS')
        }
        throw new Error('AUTH_FAILED')
      }

      const {
        token = '',
        refresh_token = '',
        custom_token = ''
      } = response.data

      _saveToken(token, refresh_token, custom_token)

      const firebaseResult = await firebaseSignInWithCustomToken(custom_token)
      if (firebaseResult === null) {
        throw new Error('FIREBASE_AUTH_FAILED')
      }

      await _getSession()

      setState('success')
    } catch (error: any) {
      setState('error')
      const errorMessage = getErrorMessage(error)
      setMessage(errorMessage)
    }
  }

  return { login, state, message }
}

export default useLogin
